export interface PlanFeature {
  text: string;
  tooltip?: string;
}

// Pricing matrix for each plan (all prices in dollars)
export interface PlanPricing {
  // Base pricing for first system
  monthly_1_visit: number;
  monthly_2_visits: number;
  annual_1_visit: number;
  annual_2_visits: number;
  // Additional system pricing
  additional_monthly_1_visit: number;
  additional_monthly_2_visits: number;
  additional_annual_1_visit: number;
  additional_annual_2_visits: number;
}

export interface Plan {
  id: string;
  code: 'standard' | 'premier' | 'optimum';
  name: string;
  tagline?: string;
  color: string;
  popular?: boolean;
  description: string;
  features: PlanFeature[];
  pricing: PlanPricing;
  allowances?: {
    hvac?: string;
    waterHeater?: string;
    appliances?: string;
    plumbing?: string;
  };
}

export interface ComparisonRow {
  feature: string;
  standard?: boolean | string;
  premier?: boolean | string;
  optimum?: boolean | string;
  tooltip?: string;
  isSection?: boolean;
}

// ---------------------------------------------------------------------------
// Plan tier definitions
// ---------------------------------------------------------------------------

// CCS & Abraham Plans (3 tiers)
export const CCS_PLANS: Plan[] = [
  {
    id: 'comfort-club',
    code: 'standard',
    name: 'Comfort Club',
    tagline: 'The Essentials',
    color: '#0066CC',
    description: 'Maintenance, priority scheduling, and member savings.',
    pricing: {
      monthly_1_visit: 16.99,
      monthly_2_visits: 33.98,
      annual_1_visit: 203.88,
      annual_2_visits: 407.76,
      additional_monthly_1_visit: 12.99,
      additional_monthly_2_visits: 25.98,
      additional_annual_1_visit: 155.88,
      additional_annual_2_visits: 311.76,
    },
    features: [
      { text: 'Premier System Tune-Up (1 or 2 per year)' },
      { text: '12-month drain pan treatment' },
      { text: 'Priority scheduling' },
      { text: 'After-hours/weekend fees waived' },
      { text: 'HVAC diagnostic fee: $69 (save $30)' },
      { text: '20% off repairs, duct cleanings, air filters, and IAQ products' },
      { text: 'Annual credit toward new system: $100/year (max $1,000)' },
      { text: 'Extended repair warranty up to 1 year' },
    ],
  },
  {
    id: 'premier',
    code: 'premier',
    name: 'Premier',
    tagline: 'Real Protection',
    color: '#00A9E0',
    popular: true,
    description: 'Expanded protection and reduced/zero diagnostic fees.',
    pricing: {
      monthly_1_visit: 39.99,
      monthly_2_visits: 79.98,
      annual_1_visit: 479.88,
      annual_2_visits: 959.76,
      additional_monthly_1_visit: 16.99,
      additional_monthly_2_visits: 33.98,
      additional_annual_1_visit: 203.88,
      additional_annual_2_visits: 407.76,
    },
    features: [
      { text: 'Everything in Comfort Club' },
      { text: 'FREE HVAC diagnostics ($0)' },
      { text: 'FREE plumbing diagnostics ($0)' },
      { text: 'Appliance diagnostic fee: $69' },
      { text: 'Annual HVAC repair allowance: up to $1,500/yr' },
      { text: 'Water heater repair allowance: up to $500/yr' },
      { text: 'Replacement credit (if unfixable): up to $1,500' },
    ],
    allowances: {
      hvac: 'Up to $1,500/yr',
      waterHeater: 'Up to $500/yr',
    },
  },
  {
    id: 'optimum',
    code: 'optimum',
    name: 'Optimum',
    tagline: 'Total Peace of Mind',
    color: '#FF6B35',
    description: 'The most comprehensive coverage across home systems.',
    pricing: {
      monthly_1_visit: 64.99,
      monthly_2_visits: 129.98,
      annual_1_visit: 779.88,
      annual_2_visits: 1559.76,
      additional_monthly_1_visit: 16.99,
      additional_monthly_2_visits: 33.98,
      additional_annual_1_visit: 203.88,
      additional_annual_2_visits: 407.76,
    },
    features: [
      { text: 'Everything in Premier' },
      { text: 'FREE diagnostics for HVAC, plumbing, and appliances ($0)' },
      { text: 'Minor plumbing repairs included' },
      { text: 'Appliance repair coverage included' },
      { text: 'Premium appliance repairs: 50% off' },
      { text: 'HVAC replacement credit: up to $3,000' },
    ],
    allowances: {
      hvac: 'Up to $1,500/yr',
      waterHeater: 'Up to $500/yr',
      plumbing: 'Minor repairs included',
      appliances: 'Included',
    },
  },
];

// Hales Plans (2 tiers)
export const HALES_PLANS: Plan[] = [
  {
    id: 'comfort-club-hales',
    code: 'standard',
    name: 'Comfort Club',
    tagline: 'The Essentials',
    color: '#005DB8',
    description: 'HVAC maintenance, priority scheduling, and member savings.',
    pricing: {
      monthly_1_visit: 16.99,
      monthly_2_visits: 33.98,
      annual_1_visit: 203.88,
      annual_2_visits: 407.76,
      additional_monthly_1_visit: 12.99,
      additional_monthly_2_visits: 25.98,
      additional_annual_1_visit: 155.88,
      additional_annual_2_visits: 311.76,
    },
    features: [
      { text: 'Premier System Tune-Up (1 or 2 per year)' },
      { text: '12-month drain pan treatment' },
      { text: 'Priority scheduling' },
      { text: 'After-hours/weekend fees waived' },
      { text: 'HVAC diagnostic fee: $69 (save $30)' },
      { text: '20% off repairs, duct cleanings, air filters, and IAQ products' },
      { text: 'Annual credit toward new system: $100/year' },
    ],
  },
  {
    id: 'premier-hales',
    code: 'premier',
    name: 'Premier',
    tagline: 'Real Protection',
    color: '#F4601B',
    popular: true,
    description: 'HVAC repair coverage + $0 diagnostics.',
    pricing: {
      // Hales Premier ALWAYS includes 2 tune-ups (no 1-visit option)
      monthly_1_visit: 39.99,
      monthly_2_visits: 39.99,
      annual_1_visit: 479.88,
      annual_2_visits: 479.88,
      // Hales additional systems pricing is DIFFERENT
      additional_monthly_1_visit: 29.98,
      additional_monthly_2_visits: 29.98,
      additional_annual_1_visit: 359.76,
      additional_annual_2_visits: 359.76,
    },
    features: [
      { text: 'Everything in Comfort Club' },
      { text: 'Includes 2 tune-ups per year' },
      { text: 'FREE HVAC diagnostics ($0)' },
      { text: 'Annual HVAC repair allowance: up to $1,500/yr' },
      { text: 'Major components covered' },
      { text: 'Replacement credit (if unfixable): up to $1,500' },
    ],
    allowances: {
      hvac: 'Up to $1,500/yr',
    },
  },
];

// Legacy export for backward compatibility
export const CLIMATE_PLANS: Plan[] = CCS_PLANS;

// ---------------------------------------------------------------------------
// Plan comparison tables (source of truth: docs/Comfort_Club_Membership_Comparison jan 12.docx)
// ---------------------------------------------------------------------------

export const CCS_COMPARISON_DATA: ComparisonRow[] = [
  { feature: 'SERVICE & MAINTENANCE', isSection: true },
  {
    feature: 'Premier System Tune-Up',
    standard: '1 or 2 per year',
    premier: '1 or 2 per year',
    optimum: '1 or 2 per year',
  },
  {
    feature: '12-Month Drain Pan Treatment',
    standard: true,
    premier: true,
    optimum: true,
  },
  {
    feature: 'Digital Maintenance Records',
    standard: true,
    premier: true,
    optimum: true,
  },
  {
    feature: 'Efficiency Checks',
    standard: true,
    premier: true,
    optimum: true,
  },
  {
    feature: 'Priority Scheduling',
    standard: true,
    premier: true,
    optimum: true,
  },

  { feature: 'DIAGNOSTICS & FEES', isSection: true },
  {
    feature: 'HVAC Diagnostic Fee',
    standard: '$69 (save $30)',
    premier: 'FREE ($0)',
    optimum: 'FREE ($0)',
  },
  {
    feature: 'Plumbing Diagnostic Fee',
    standard: '$69',
    premier: 'FREE ($0)',
    optimum: 'FREE ($0)',
  },
  {
    feature: 'Appliance Diagnostic Fee',
    standard: '$69',
    premier: '$69',
    optimum: 'FREE ($0)',
  },
  {
    feature: 'After-Hours/Weekend Fees',
    standard: 'Waived',
    premier: 'Waived',
    optimum: 'Waived',
  },

  { feature: 'DISCOUNTS & SAVINGS', isSection: true },
  {
    feature: 'Discount on HVAC Repairs',
    standard: '20% off',
    premier: 'Covered Up to $1,500/yr',
    optimum: 'Covered Up to $1,500/yr',
  },
  {
    feature: 'Discount on Plumbing Repairs',
    standard: '20% off',
    premier: 'Covered Up to $1,500/yr',
    optimum: 'Covered Up to $1,500/yr',
  },
  {
    feature: 'Discount on Appliance Repairs',
    standard: '20% off',
    premier: '20% off',
    optimum: 'Covered Up to $1,500/yr',
  },
  {
    feature: 'Discount on Duct Cleanings',
    standard: '20% off',
    premier: '20% off',
    optimum: '30% off',
  },
  {
    feature: 'Discount on Air Filters',
    standard: '20% off',
    premier: '20% off',
    optimum: '30% off',
  },
  {
    feature: 'Discount on UV Lights & IAQ',
    standard: '20% off',
    premier: '20% off',
    optimum: '30% off',
  },
  {
    feature: 'Premium Appliance Repairs',
    standard: '—',
    premier: '—',
    optimum: '50% off',
  },

  { feature: 'HVAC REPAIR COVERAGE', isSection: true },
  {
    feature: 'Annual HVAC Repair Allowance',
    standard: '—',
    premier: 'Up to $1,500/yr',
    optimum: 'Up to $1,500/yr',
  },
  {
    feature: 'Major Components Covered',
    standard: false,
    premier: true,
    optimum: true,
  },
  {
    feature: '• Compressor',
    standard: false,
    premier: true,
    optimum: true,
  },
  {
    feature: '• Motors',
    standard: false,
    premier: true,
    optimum: true,
  },
  {
    feature: '• Contactors & Capacitors',
    standard: false,
    premier: true,
    optimum: true,
  },
  {
    feature: '• Circuit Boards',
    standard: false,
    premier: true,
    optimum: true,
  },
  {
    feature: '• Relays, Drain Pans, Fan Blades',
    standard: false,
    premier: true,
    optimum: true,
  },

  { feature: 'WATER HEATER COVERAGE', isSection: true },
  {
    feature: 'Annual Repair Allowance',
    standard: '—',
    premier: 'Up to $500/yr',
    optimum: 'Up to $500/yr',
  },
  {
    feature: 'Replacement Credit (if unfixable)',
    standard: '—',
    premier: 'Up to $500',
    optimum: 'Up to $500',
  },

  { feature: 'MINOR PLUMBING COVERAGE', isSection: true },
  {
    feature: 'Minor Plumbing Repairs Included',
    standard: false,
    premier: true,
    optimum: true,
  },
  {
    feature: '• Leaky Faucets',
    standard: false,
    premier: true,
    optimum: true,
  },
  {
    feature: '• Clogged Sink Drains',
    standard: false,
    premier: true,
    optimum: true,
  },
  {
    feature: '• Running Toilets',
    standard: false,
    premier: true,
    optimum: true,
  },
  {
    feature: '• Loose/Worn Fixtures',
    standard: false,
    premier: true,
    optimum: true,
  },
  {
    feature: '• Minor Pipe Leaks',
    standard: false,
    premier: true,
    optimum: true,
  },
  {
    feature: '• Toilet Flange Repair',
    standard: false,
    premier: true,
    optimum: true,
  },
  {
    feature: '• Garbage Disposal Issues',
    standard: false,
    premier: true,
    optimum: true,
  },

  { feature: 'APPLIANCE COVERAGE (Optimum Only)', isSection: true },
  {
    feature: 'Refrigerator Repairs',
    standard: false,
    premier: false,
    optimum: 'Up to $500/yr',
  },
  {
    feature: 'Oven/Range Repairs',
    standard: false,
    premier: false,
    optimum: 'Up to $500/yr',
  },
  {
    feature: 'Clothes Washer Repairs',
    standard: false,
    premier: false,
    optimum: 'Up to $500/yr',
  },
  {
    feature: 'Clothes Dryer Repairs',
    standard: false,
    premier: false,
    optimum: 'Up to $500/yr',
  },
  {
    feature: 'Dishwasher Repairs',
    standard: false,
    premier: false,
    optimum: 'Up to $300/yr',
  },
  {
    feature: 'Built-In Microwave Repairs',
    standard: false,
    premier: false,
    optimum: 'Up to $200/yr',
  },

  { feature: 'REPLACEMENT CREDITS (If Unfixable)', isSection: true },
  {
    feature: 'Central AC System',
    standard: false,
    premier: 'Up to $1,500',
    optimum: 'Up to $3,000',
  },
  {
    feature: 'Water Heater',
    standard: false,
    premier: 'Up to $500',
    optimum: 'Up to $500',
  },
  {
    feature: 'Refrigerator',
    standard: false,
    premier: false,
    optimum: 'Up to $500',
  },
  {
    feature: 'Clothes Washer/Dryer',
    standard: false,
    premier: false,
    optimum: 'Up to $500 each',
  },
  {
    feature: 'Dishwasher',
    standard: false,
    premier: false,
    optimum: 'Up to $300',
  },
  {
    feature: 'Microwave',
    standard: false,
    premier: false,
    optimum: 'Up to $200',
  },
  {
    feature: 'Oven/Range/Cooktop',
    standard: false,
    premier: false,
    optimum: 'Up to $500',
  },
  {
    feature: 'Garbage Disposal',
    standard: false,
    premier: false,
    optimum: 'Up to $250',
  },

  { feature: 'WARRANTIES & LOYALTY CREDITS', isSection: true },
  {
    feature: 'Extended Repair Warranty',
    standard: 'Up to 1 year',
    premier: 'Up to 1 year',
    optimum: 'Up to 1 year',
  },
  {
    feature: 'Annual Credit Toward New System',
    standard: '$100/year',
    premier: '$100/year',
    optimum: '$100/year',
  },
  {
    feature: 'Maximum Accumulation',
    standard: 'Up to $1,000',
    premier: 'Up to $1,000',
    optimum: 'Up to $1,000',
  },

  { feature: 'MEMBER PERKS', isSection: true },
  {
    feature: 'Dedicated Member Phone Line',
    standard: true,
    premier: true,
    optimum: true,
  },
  {
    feature: 'Exclusive Member Promotions',
    standard: true,
    premier: true,
    optimum: true,
  },
  {
    feature: 'Partner Discounts',
    standard: true,
    premier: true,
    optimum: true,
  },
  {
    feature: 'Convenient Auto-Renewal',
    standard: true,
    premier: true,
    optimum: true,
  },
];

export const HALES_COMPARISON_DATA: ComparisonRow[] = [
  { feature: 'SERVICE & MAINTENANCE', isSection: true },
  {
    feature: 'Premier System Tune-Up',
    standard: '1 or 2 per year',
    premier: '2 per year',
  },
  {
    feature: '12-Month Drain Pan Treatment',
    standard: true,
    premier: true,
  },
  {
    feature: 'Digital Maintenance Records',
    standard: true,
    premier: true,
  },
  {
    feature: 'Efficiency Checks',
    standard: true,
    premier: true,
  },
  {
    feature: 'Priority Scheduling',
    standard: true,
    premier: true,
  },

  { feature: 'DIAGNOSTICS & FEES', isSection: true },
  {
    feature: 'HVAC Diagnostic Fee',
    standard: '$69 (save $30)',
    premier: 'FREE ($0)',
  },
  {
    feature: 'After-Hours/Weekend Fees',
    standard: 'Waived',
    premier: 'Waived',
  },

  { feature: 'DISCOUNTS & SAVINGS', isSection: true },
  {
    feature: 'Discount on Repairs',
    standard: '20% off',
    premier: '20% off',
  },
  {
    feature: 'Discount on Duct Cleanings',
    standard: '20% off',
    premier: '20% off',
  },
  {
    feature: 'Discount on Air Filters',
    standard: '20% off',
    premier: '20% off',
  },
  {
    feature: 'Discount on UV Lights & IAQ Products',
    standard: '20% off',
    premier: '20% off',
  },

  { feature: 'REPAIR COVERAGE', isSection: true },
  {
    feature: 'Annual HVAC Repair Allowance',
    standard: false,
    premier: 'Up to $1,500/year',
  },
  {
    feature: 'Major Components Covered',
    standard: false,
    premier: true,
  },
  {
    feature: '• Compressor',
    standard: false,
    premier: true,
  },
  {
    feature: '• Motors',
    standard: false,
    premier: true,
  },
  {
    feature: '• Contactors & Capacitors',
    standard: false,
    premier: true,
  },
  {
    feature: '• Circuit Boards',
    standard: false,
    premier: true,
  },
  {
    feature: '• Relays, Drain Pans, Fan Blades',
    standard: false,
    premier: true,
  },
  { feature: 'WARRANTIES & CREDITS', isSection: true },
  {
    feature: 'Extended Repair Warranty',
    standard: 'Up to 1 year',
    premier: 'Up to 1 year',
  },
  {
    feature: 'Annual Credit Toward New System',
    standard: '$100/year',
    premier: '$100/year',
  },
  {
    feature: 'Replacement Credit (if unfixable)',
    standard: false,
    premier: 'Up to $1,500',
  },

  { feature: 'MEMBER PERKS', isSection: true },
  {
    feature: 'Dedicated Member Phone Line',
    standard: true,
    premier: true,
  },
  {
    feature: 'Exclusive Member Promotions',
    standard: true,
    premier: true,
  },
  {
    feature: 'Partner Discounts',
    standard: true,
    premier: true,
  },
  {
    feature: 'Convenient Auto-Renewal',
    standard: true,
    premier: true,
  },
];

// Backwards-compatible alias
export const COMPARISON_DATA: ComparisonRow[] = CCS_COMPARISON_DATA;
